import React from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import CopyableText, { CopyablePrompt } from './CopyableText';
import {
    CODEX_LOGIN_COMMAND,
    FIRST_QUESTIONS,
    claudeAddConnectorUrl,
    CLAUDE_CONNECTORS_URL,
    CLAUDE_ORGANISATION_CONNECTORS_URL,
    CLAUDE_SETUP_HELP_URL,
    claudeOwnerInstructions,
    claudeCodeAddCommand,
    codexAddCommand,
    CHATGPT_PLUGINS_URL,
    OPENAI_ADMIN_URL,
    OPENAI_PLUGIN_SETUP_HELP,
    OPENAI_PLUGIN_ADMIN_HELP,
    OPENAI_MCP_HELP,
    OPENAI_MANAGED_MCP_HELP,
    chatGptAdminInstructions,
    codexAdminInstructions,
    codexMcpRequirement,
} from './connectInstructions';
import { SUPPORT_EMAIL } from '../../utils/moduleAccess';
import InAppLink from '../ui/InAppLink';
import { ROUTE_PATTERNS } from '../../router/routes';
import claudeLogo from '../../assets/assistant-logos/claude.svg';
import openaiLogo from '../../assets/assistant-logos/openai.svg';
// Official asset: https://adoption.microsoft.com/wp-content/uploads/2023/09/icon-copilot.svg
import copilotLogo from '../../assets/assistant-logos/copilot.svg';

interface ConnectAssistantPageProps {
    /** This environment's MCP server URL (see `mcpServerUrl`). */
    serverUrl: string;
    /** The signed-in account: the one to sign in with when the assistant asks. */
    email?: string | null;
    /** Shown under the sign-in line when config did not load (see ConnectRoute). */
    configNote?: string;
    /** One-time confirmation shown after an invited user sets their password. */
    welcome?: boolean;
    /** Set where Chat is open to this account (free plan served by chat-free):
     *  runs when the person follows the Chat entry. Absent = no Chat entry. */
    onOpenChat?: () => void;
}

// The assistants BARK connects to, in the order the page offers them.
// Logos: Anthropic's Claude spark and OpenAI's blossom, unchanged from the
// press kits, kept smaller than BARK's own mark. The OpenAI file pads its mark
// to half the canvas, so it renders at twice the box, with the margin taken
// back, to match the Claude mark. `connectedWhen` is where the assistant lists
// BARK once the connection works.
const CLAUDE_LOGO = { logo: claudeLogo, logoClass: 'h-4 w-4' };
const OPENAI_LOGO = { logo: openaiLogo, logoClass: '-m-2 h-8 w-8' };
const ASSISTANTS = [
    { id: 'claude', label: 'Claude', ...CLAUDE_LOGO, connectedWhen: 'BARK shows in Connectors' },
    { id: 'claude-code', label: 'Claude Code', ...CLAUDE_LOGO, connectedWhen: 'BARK shows in /mcp' },
    { id: 'chatgpt', label: 'ChatGPT', ...OPENAI_LOGO, connectedWhen: 'BARK shows in Plugins' },
    { id: 'codex', label: 'Codex', ...OPENAI_LOGO, connectedWhen: 'BARK shows in /mcp' },
    { id: 'copilot', label: 'Copilot', logo: copilotLogo, logoClass: 'h-4 w-4', connectedWhen: '' },
] as const;

type AssistantId = (typeof ASSISTANTS)[number]['id'];

// Kawi's 2026-10-17 design, on DESIGN.md tokens: his navy is `primary`, his
// page grey `bg` / `surface-sunken`, his teal accent `primary` (DESIGN.md keeps
// teal for graph nodes), his copied state `success`.
const motion = 'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]';
const focusRing = 'focus-visible:outline-none focus-visible:shadow-bark-focus';
const bodyClass = 'font-body text-[15px] leading-[1.5] text-fg-secondary';
const mutedClass = 'font-body text-[13px] leading-[1.5] text-fg-tertiary';
const cardClass = 'flex flex-col gap-4';
const rowClass = 'flex flex-col gap-3';
const stepListClass = `list-decimal space-y-2 pl-6 marker:text-fg-tertiary ${bodyClass}`;
const eyebrowClass = 'font-body text-[13px] font-medium leading-[1.5] text-fg-tertiary';
const fieldLabelClass = 'font-body text-[13px] font-medium leading-[1.5] text-fg-tertiary';

/** A name on the assistant's own screen: a menu, a field or a button. */
function OnScreen({ children }: { children: React.ReactNode }) {
    return <span className="font-semibold text-fg">{children}</span>;
}

/** A value the person types in. */
function Typed({ children }: { children: React.ReactNode }) {
    return <code className="font-mono text-[13px] font-medium text-fg">{children}</code>;
}

function NewTabHint() {
    return <span className="sr-only"> (opens in a new tab)</span>;
}

/** The server URL, labelled, for the steps that say "the server URL above". */
function ServerUrlField({ serverUrl }: { serverUrl: string }) {
    return (
        <div className="flex flex-col gap-1.5">
            <p className={fieldLabelClass}>Server URL</p>
            <CopyableText text={serverUrl} label="server URL" tone="sunken" />
        </div>
    );
}

type ClaudePath = 'personal' | 'member' | 'owner';
function savedConnectionPath(key: string): ClaudePath | null {
    try {
        const value = window.localStorage.getItem(key);
        return value === 'personal' || value === 'member' || value === 'owner' ? value : null;
    } catch { return null; }
}

function useConnectionPath(assistant: string) {
    const key = `bark-${assistant}-connection-path`;
    const [path, setPath] = React.useState<ClaudePath | null>(() => savedConnectionPath(key));
    const choose = (value: ClaudePath) => {
        setPath(value);
        try { window.localStorage.setItem(key, value); } catch { /* Storage is optional. */ }
    };
    return [path, choose] as const;
}

function AccountPathSelector({ assistant, path, onChange, teamLabel = 'Business / Enterprise' }: {
    assistant: string; path: ClaudePath | null; onChange: (path: ClaudePath) => void; teamLabel?: string;
}) {
    const id = React.useId();
    const team = path === 'member' || path === 'owner';
    const option = (label: string, value: ClaudePath, checked: boolean, group: string) => (
        <label className="relative cursor-pointer">
            <input type="radio" name={`${id}-${group}`} checked={checked}
                onChange={() => onChange(value)} className="peer sr-only" />
            <span className={`flex min-h-10 items-center justify-center rounded-lg px-2 font-body text-[13px] font-medium ${motion} peer-focus-visible:shadow-bark-focus ${
                checked ? 'bg-surface text-fg shadow-bark-xs' : 'text-fg-secondary hover:text-fg'
            }`}>{label}</span>
        </label>
    );
    return (
        <div className="flex flex-col gap-5 border-b border-divider pb-5">
            <fieldset>
                <legend className={`mb-2 w-full ${fieldLabelClass}`}>Which {assistant} account do you use?</legend>
                <div className="grid grid-cols-2 gap-1 rounded-xl bg-bg p-1">
                    {option('Personal account', 'personal', path === 'personal', 'plan')}
                    {option(teamLabel, 'member', team, 'plan')}
                </div>
            </fieldset>
            <div className="border-t border-divider pt-4"><fieldset>
                    <legend className={`mb-2 w-full ${fieldLabelClass}`}>What would you like to do?<span className="sr-only"> in {assistant}</span></legend>
                    <div className="grid grid-cols-2 gap-1 rounded-xl bg-bg p-1">
                        {option('Connect my account', path === 'personal' ? 'personal' : 'member', path === 'member' || path === 'personal', 'role')}
                        {option('Organisation setup', 'owner', path === 'owner', 'role')}
                    </div>
            </fieldset></div>
        </div>
    );
}

function ClaudeCard({ serverUrl, path, onChange }: {
    serverUrl: string; path: ClaudePath | null; onChange: (path: ClaudePath) => void;
}) {
    const href = path === 'owner' ? CLAUDE_ORGANISATION_CONNECTORS_URL
        : path === 'member' ? CLAUDE_CONNECTORS_URL : claudeAddConnectorUrl(serverUrl);
    const label = path === 'owner' ? 'Set up BARK for your organisation'
        : path === 'member' ? 'Connect to BARK in Claude' : 'Add to Claude';
    return (
        <div className={cardClass}>
            <AccountPathSelector assistant="Claude" path={path} onChange={onChange} teamLabel="Team / Enterprise" />
            {path && (
                <>
                    <a href={href} target="_blank" rel="noopener noreferrer"
                        className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 font-body text-[15px] font-semibold text-on-primary hover:bg-primary-hover active:translate-y-px ${motion} ${focusRing}`}>
                        {label}<ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /><NewTabHint />
                    </a>
                    {path === 'member' && <ol className={stepListClass}>
                        <li>In Claude, open <OnScreen>Customize → Connectors</OnScreen>.</li>
                        <li>Find BARK and select <OnScreen>Connect</OnScreen>.</li>
                        <li>Sign in with your own BARK account.</li>
                    </ol>}
                    {path === 'owner' && <p className={bodyClass}>An Owner or Primary Owner adds BARK once. Each member then connects their own account.</p>}
                </>
            )}
            <ClaudeMore serverUrl={serverUrl} path={path} />
        </div>
    );
}

/** One "Set up for a team" / "Add manually" disclosure under the Claude panel. */
function More({ title, children, open = false }: { title: string; children: React.ReactNode; open?: boolean }) {
    return (
        <details className="group border-t border-divider pt-2" open={open}>
            {/* min-h-11 keeps the 44px hit target; list-none + the webkit rule drop the native marker. */}
            <summary
                className={`flex min-h-11 cursor-pointer list-none items-center gap-2 rounded font-body text-[15px] font-medium text-primary [&::-webkit-details-marker]:hidden ${focusRing}`}
            >
                {title}
                <ChevronRight
                    className={`h-3 w-3 group-open:rotate-90 ${motion}`}
                    strokeWidth={1.5}
                    aria-hidden="true"
                />
            </summary>
            <div className="flex flex-col gap-3.5 pt-2">{children}</div>
        </details>
    );
}

function ClaudeMore({ serverUrl, path }: { serverUrl: string; path: ClaudePath | null }) {
    if (!path) return null;
    return (
        <div className="flex w-full flex-col gap-2">
            {path === 'member' ? (
                <More title="BARK isn't listed" key="member">
                    <p className={bodyClass}>Ask your Claude organisation owner to add BARK. Copy these instructions to send to them.</p>
                    <CopyableText text={claudeOwnerInstructions(serverUrl)} label="owner instructions" buttonText="Copy instructions" tone="sunken" textStyle="prose" />
                </More>
            ) : (
                <More title={path === 'owner' ? 'Organisation setup instructions' : 'Add manually'} open={path === 'owner'} key={path}>
                    {path === 'owner' && <p className={bodyClass}>You need to be an Owner or Primary Owner in Claude. Enterprise also allows a custom role with Manage access to Libraries.</p>}
                    <ServerUrlField serverUrl={serverUrl} />
                    <ol className={stepListClass}>
                        <li>Open <OnScreen>{path === 'owner' ? 'Organization settings → Connectors' : 'Customize → Connectors'}</OnScreen> in Claude.</li>
                        <li>Select <OnScreen>{path === 'owner' ? 'Add → Custom → Web' : 'Add → Add custom connector'}</OnScreen>.</li>
                        <li>Name it <Typed>BARK</Typed> and paste the server URL above.</li>
                        <li>Select <OnScreen>Continue</OnScreen>, review the authentication settings and follow the prompts. Select <OnScreen>Add</OnScreen> to finish.</li>
                        <li>{path === 'owner' ? 'Each member then opens Customize → Connectors → BARK → Connect and signs in with their own BARK account.' : 'Select Connect and sign in with your own BARK account.'}</li>
                    </ol>
                    <a href={CLAUDE_SETUP_HELP_URL} target="_blank" rel="noopener noreferrer" className={`${mutedClass} rounded text-primary underline ${focusRing}`}>Claude's full setup guide<NewTabHint /></a>
                </More>
            )}
        </div>
    );
}

function DesktopChatGptSetup({ serverUrl }: { serverUrl: string }) {
    return (
        <div className={cardClass}>
            <div className={rowClass}>
                <ServerUrlField serverUrl={serverUrl} />
            </div>
            <div className={rowClass}>
                <p className={bodyClass}>In the ChatGPT desktop app:</p>
                <ol className={stepListClass}>
                    <li>
                        Open <OnScreen>Settings → MCP servers</OnScreen>.
                    </li>
                    <li>
                        Select <OnScreen>Add server</OnScreen>.
                    </li>
                    <li>
                        Set <OnScreen>Name</OnScreen> to <Typed>bark</Typed>, <OnScreen>Type</OnScreen> to{' '}
                        <OnScreen>Streamable HTTP</OnScreen>, and <OnScreen>URL</OnScreen> to the server URL above.
                        Leave the token and header fields empty.
                    </li>
                    <li>
                        Select <OnScreen>Save</OnScreen>, then <OnScreen>Restart</OnScreen>. Select <OnScreen>Authenticate</OnScreen> next to{' '}
                        <Typed>bark</Typed>, and sign in to BARK in the browser.
                    </li>
                    <li>
                        In the composer, type <Typed>/mcp</Typed> to check the connection.
                    </li>
                </ol>
            </div>
        </div>
    );
}

function SetupLink({ href, children }: { href: string; children: React.ReactNode }) {
    return <a href={href} target="_blank" rel="noopener noreferrer"
        className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 font-body text-[15px] font-semibold text-on-primary hover:bg-primary-hover active:translate-y-px ${motion} ${focusRing}`}>
        {children}<ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /><NewTabHint />
    </a>;
}

function SetupHelp({ href, children }: { href: string; children: React.ReactNode }) {
    return <a href={href} target="_blank" rel="noopener noreferrer"
        className={`${mutedClass} rounded text-primary underline ${focusRing}`}>{children}<NewTabHint /></a>;
}

function ChatGptPluginSetup({ serverUrl }: { serverUrl: string }) {
    return <div className={cardClass}>
        <ServerUrlField serverUrl={serverUrl} />
        <ol className={stepListClass}>
            <li>In <OnScreen>Plugins</OnScreen>, select <OnScreen>+ → Add custom MCP server</OnScreen>.</li>
            <li>Name it <Typed>BARK</Typed>, add a description and enter the server URL above.</li>
            <li>Configure authentication and review the prompts. Select <OnScreen>Create as a plugin</OnScreen>.</li>
            <li>Review the discovered tools, install the plugin and complete the BARK sign-in prompts.</li>
            <li>Start a new conversation and select BARK with <Typed>@</Typed>.</li>
        </ol>
        <SetupHelp href={OPENAI_PLUGIN_SETUP_HELP}>OpenAI's plugin setup guide</SetupHelp>
    </div>;
}

function ManagedMcpSetup({ serverUrl }: { serverUrl: string }) {
    return <div className={cardClass}>
        <p className={bodyClass}>If your organisation restricts MCP servers, approve the name <Typed>bark</Typed> and this exact URL in its existing policy.</p>
        <CopyableText text={codexMcpRequirement(serverUrl)} label="MCP policy entry" tone="sunken" />
        <p className={bodyClass}>Add this entry to the existing <Typed>requirements.toml</Typed> policy. Keep the other approved servers. Approval alone does not install BARK or sign anyone in.</p>
        <p className={bodyClass}>Provide the server configuration to members, or let them add it after approval. Each person then authenticates their own BARK account.</p>
        <SetupHelp href={OPENAI_MANAGED_MCP_HELP}>OpenAI's managed MCP guide</SetupHelp>
    </div>;
}

function ChatGptCard({ serverUrl }: { serverUrl: string }) {
    const [path, choosePath] = useConnectionPath('chatgpt');
    return <div className={cardClass}>
        <AccountPathSelector assistant="ChatGPT" path={path} onChange={choosePath} />
        {path === 'member' && <>
            <SetupLink href={CHATGPT_PLUGINS_URL}>Open ChatGPT plugins</SetupLink>
            <ol className={stepListClass}>
                <li>Open your company workspace and find the BARK plugin your admin has shared or made available.</li>
                <li>Install it if needed, select <OnScreen>Connect</OnScreen> and sign in to BARK.</li>
                <li>Start a new conversation and select BARK with <Typed>@</Typed>.</li>
            </ol>
            <More title="BARK isn't available">
                <p className={bodyClass}>Ask your workspace admin to make BARK available to your account.</p>
                <CopyableText text={chatGptAdminInstructions(serverUrl)} label="ChatGPT admin instructions" buttonText="Copy instructions" tone="sunken" textStyle="prose" />
            </More>
            <More title="My company uses a local desktop connection">
                <p className={bodyClass}>Follow your company's approved setup. If BARK is already configured, authenticate it. Otherwise, add it below only when your policy permits it.</p>
                <DesktopChatGptSetup serverUrl={serverUrl} />
                <SetupHelp href={OPENAI_MCP_HELP}>OpenAI's desktop MCP guide</SetupHelp>
            </More>
        </>}
        {path === 'personal' && <>
            <SetupLink href={CHATGPT_PLUGINS_URL}>Open ChatGPT plugins</SetupLink>
            <p className={bodyClass}>Add BARK as a custom plugin where your account permits custom MCP servers.</p>
            <ChatGptPluginSetup serverUrl={serverUrl} />
            <More title="Set up locally in the desktop app">
                <DesktopChatGptSetup serverUrl={serverUrl} />
                <SetupHelp href={OPENAI_MCP_HELP}>OpenAI's desktop MCP guide</SetupHelp>
            </More>
        </>}
        {path === 'owner' && <>
            <SetupLink href={OPENAI_ADMIN_URL}>Open OpenAI admin console</SetupLink>
            <ol className={stepListClass}>
                <li>Select the intended workspace. In <OnScreen>Permissions & roles</OnScreen>, check who can create plugins with MCPs, share plugins and publish plugins to the workspace.</li>
                <li>If BARK is not already available, create its custom plugin using the setup instructions below, then publish or share it with the intended members.</li>
                <li>In <OnScreen>Plugins</OnScreen>, make BARK available for installation or install it for the workspace. Check member permissions and access to its underlying app.</li>
                <li>Have a member connect their own BARK account and test a question before rolling it out.</li>
            </ol>
            <More title="Create the BARK plugin">
                <SetupHelp href={CHATGPT_PLUGINS_URL}>Open ChatGPT plugins</SetupHelp>
                <ChatGptPluginSetup serverUrl={serverUrl} />
            </More>
            <More title="Manage local desktop connections">
                <ManagedMcpSetup serverUrl={serverUrl} />
            </More>
            <SetupHelp href={OPENAI_PLUGIN_ADMIN_HELP}>OpenAI's workspace plugin guide</SetupHelp>
        </>}
    </div>;
}

function CodexCommands({ serverUrl }: { serverUrl: string }) {
    return (
        <ol className={stepListClass}>
            <li>
                <p className={bodyClass}>For the Codex CLI or IDE extension, run this in a terminal:</p>
                <CopyableText text={codexAddCommand(serverUrl)} label="Codex command" tone="sunken" />
            </li>
            <li>
                <p className={bodyClass}>Sign-in usually starts by itself. If it does not, run:</p>
                <CopyableText text={CODEX_LOGIN_COMMAND} label="Codex sign-in command" tone="sunken" />
            </li>
        </ol>
    );
}

function CodexCard({ serverUrl }: { serverUrl: string }) {
    const [path, choosePath] = useConnectionPath('codex');
    return <div className={cardClass}>
        <AccountPathSelector assistant="Codex" path={path} onChange={choosePath} />
        {path === 'personal' && <>
            <CodexCommands serverUrl={serverUrl} />
            <SetupHelp href={OPENAI_MCP_HELP}>OpenAI's MCP setup guide</SetupHelp>
        </>}
        {path === 'member' && <>
            <ol className={stepListClass}>
            <li><p>Use your company's approved MCP setup. If your admin has already configured BARK, sign in:</p>
            <CopyableText text={CODEX_LOGIN_COMMAND} label="Codex sign-in command" tone="sunken" />
            </li>
            <li>Use <Typed>/mcp</Typed> in Codex to check the connection.</li>
            </ol>
            <More title="BARK isn't configured on my computer">
                <p className={bodyClass}>If your organisation allows you to add BARK, run:</p>
                <CopyableText text={codexAddCommand(serverUrl)} label="Codex command" tone="sunken" />
                <p className={bodyClass}>Then authenticate BARK. If your company policy blocks it, ask your admin to approve it.</p>
            </More>
            <More title="Ask my admin to approve BARK">
                <CopyableText text={codexAdminInstructions(serverUrl)} label="Codex admin instructions" buttonText="Copy instructions" tone="sunken" textStyle="prose" />
            </More>
            <SetupHelp href={OPENAI_MCP_HELP}>OpenAI's MCP setup guide</SetupHelp>
        </>}
        {path === 'owner' && <>
            <SetupLink href={OPENAI_ADMIN_URL}>Open OpenAI admin console</SetupLink>
            <ManagedMcpSetup serverUrl={serverUrl} />
            <More title="Member setup commands">
                <CodexCommands serverUrl={serverUrl} />
            </More>
        </>}
    </div>;
}

function ClaudeCodeCard({ serverUrl }: { serverUrl: string }) {
    return (
        <div className={cardClass}>
            <div className={rowClass}>
                <p className={bodyClass}>Run this in a terminal:</p>
                <CopyableText text={claudeCodeAddCommand(serverUrl)} label="Claude Code command" tone="sunken" />
            </div>
            <div className={rowClass}>
                <p className={bodyClass}>
                    In Claude Code, run <Typed>/mcp</Typed>, choose <Typed>bark</Typed>, then{' '}
                    <OnScreen>Authenticate</OnScreen>.
                </p>
            </div>
        </div>
    );
}

/**
 * The connect page (`/connect`): how to add BARK's MCP server to an AI
 * assistant, and a first question to ask there. Kawi's design of 2026-10-17.
 *
 * Presentational only. It makes no request, so an account with every web
 * module off (a free signup) can open it: the view route supplies the server
 * URL from runtime config, and the email from the session.
 */
export default function ConnectAssistantPage({ serverUrl, email, configNote, welcome = false, onOpenChat }: ConnectAssistantPageProps) {
    const baseId = React.useId();
    const [active, setActive] = React.useState<AssistantId>('claude');
    const [claudePath, chooseClaudePath] = useConnectionPath('claude');
    const tabRefs = React.useRef<Partial<Record<AssistantId, HTMLButtonElement | null>>>({});

    const tabId = (id: AssistantId) => `${baseId}-tab-${id}`;
    const panelId = (id: AssistantId) => `${baseId}-panel-${id}`;

    // WAI-ARIA tabs: arrow keys move between tabs and select them; Home and
    // End jump to the ends. Only the selected tab is in the Tab order.
    const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const last = ASSISTANTS.length - 1;
        let next: number;
        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        else return;
        event.preventDefault();
        const nextId = ASSISTANTS[next].id;
        setActive(nextId);
        tabRefs.current[nextId]?.focus();
    };

    return (
        <div className="h-full overflow-y-auto bg-bg">
            <div className="mx-auto flex w-full max-w-[880px] flex-col gap-14 px-4 pb-20 pt-10">
                {welcome && (
                    <p role="status" className={`${bodyClass} text-center`}>
                        Your account is ready. BARK works inside your AI assistant — connect it below.
                    </p>
                )}
                <header className="flex flex-col items-center gap-4 text-center">
                    <span className="font-mono text-[13px] uppercase text-fg-secondary">
                        Connect your assistant
                    </span>
                    <h1 className="font-heading text-[28px] font-normal leading-[1.2] tracking-[-0.01em] text-fg sm:text-[32px]">
                        Connect BARK to your assistant
                    </h1>
                    <p className="max-w-[600px] font-body text-[17px] leading-[1.6] text-fg-secondary">
                        Connect once, then ask your regulatory questions inside the assistant you already use. Answers
                        come with sources.
                    </p>
                    {configNote && (
                        <p role="status" className={bodyClass}>
                            {configNote}
                        </p>
                    )}
                    {onOpenChat && (
                        <div className="flex w-full max-w-[560px] flex-col gap-3">
                            <InAppLink
                                href={ROUTE_PATTERNS.lookup}
                                onNavigate={onOpenChat}
                                className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 font-body text-[15px] font-semibold text-on-primary hover:bg-primary-hover active:translate-y-px ${motion} ${focusRing}`}
                            >
                                Open Chat
                                <ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                            </InAppLink>
                            <p className={mutedClass}>Ask a regulatory question here, with sources. Or connect an assistant below.</p>
                        </div>
                    )}
                </header>

                <section aria-labelledby={`${baseId}-add`} className="flex flex-col gap-5">
                    <h2 id={`${baseId}-add`} className="font-body text-[24px] font-semibold leading-[1.3] text-fg">
                        1. Add BARK
                    </h2>
                    <div className="flex flex-col items-center gap-6 rounded-2xl border border-border-default bg-surface p-5 sm:p-8">
                        <div
                            role="tablist"
                            aria-label="Assistant"
                            className="grid w-full grid-cols-2 gap-1 rounded-xl bg-bg p-1 sm:grid-cols-5"
                        >
                            {ASSISTANTS.map((assistant, index) => {
                                const selected = assistant.id === active;
                                return (
                                    <button
                                        key={assistant.id}
                                        ref={(node) => {
                                            tabRefs.current[assistant.id] = node;
                                        }}
                                        id={tabId(assistant.id)}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        aria-controls={panelId(assistant.id)}
                                        tabIndex={selected ? 0 : -1}
                                        onClick={() => setActive(assistant.id)}
                                        onKeyDown={(event) => onTabKeyDown(event, index)}
                                        className={`inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-2 font-body text-[15px] font-medium ${assistant.id === 'copilot' ? 'col-span-2 sm:col-span-1' : ''} ${motion} ${focusRing} ${
                                            selected
                                                ? 'bg-surface text-fg shadow-bark-xs'
                                                : 'text-fg-secondary hover:text-fg'
                                        }`}
                                    >
                                        {assistant.logo && <img
                                            src={assistant.logo}
                                            alt=""
                                            className={`${assistant.logoClass} shrink-0 object-contain ${selected ? '' : 'opacity-70'}`}
                                        />}
                                        {assistant.label}
                                    </button>
                                );
                            })}
                        </div>
                        {ASSISTANTS.map((assistant) => (
                            <div
                                key={assistant.id}
                                id={panelId(assistant.id)}
                                role="tabpanel"
                                aria-labelledby={tabId(assistant.id)}
                                tabIndex={0}
                                hidden={assistant.id !== active}
                                className={`w-full max-w-[560px] rounded-lg ${focusRing}`}
                            >
                                <div className="flex flex-col gap-6">
                                    <h3 className="font-body text-[15px] font-semibold leading-[1.5] text-fg">
                                        {assistant.id === 'copilot' ? 'Microsoft 365 Copilot' : assistant.label}
                                    </h3>
                                    {assistant.id === 'claude' && <ClaudeCard serverUrl={serverUrl} path={claudePath} onChange={chooseClaudePath} />}
                                    {assistant.id === 'chatgpt' && <ChatGptCard serverUrl={serverUrl} />}
                                    {assistant.id === 'copilot' && (
                                        <div className={cardClass}>
                                            <p className={bodyClass}>
                                                Speak to your BARK contact to get connected.
                                            </p>
                                            <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('BARK for Microsoft 365 Copilot')}`}
                                                className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 font-body text-[15px] font-semibold text-on-primary hover:bg-primary-hover active:translate-y-px ${motion} ${focusRing}`}>
                                                Contact BARK<ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                                            </a>
                                        </div>
                                    )}
                                    {assistant.id === 'codex' && <CodexCard serverUrl={serverUrl} />}
                                    {assistant.id === 'claude-code' && <ClaudeCodeCard serverUrl={serverUrl} />}
                                    {assistant.id !== 'copilot' && <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
                                        <div className="flex flex-col gap-1 rounded-lg bg-surface-sunken px-4 py-3.5">
                                            <span className={eyebrowClass}>Sign in as</span>
                                            <strong className="break-words font-body text-[15px] font-medium text-fg">
                                                {/* A long address wraps after the @, not mid-word. */}
                                                {email ? (
                                                    <>
                                                        {email.split('@')[0]}@<wbr />
                                                        {email.split('@').slice(1).join('@')}
                                                    </>
                                                ) : (
                                                    'Your BARK account'
                                                )}
                                            </strong>
                                        </div>
                                        <div className="flex flex-col gap-1 rounded-lg bg-surface-sunken px-4 py-3.5">
                                            <span className={eyebrowClass}>Connected when</span>
                                            <strong className="font-body text-[15px] font-medium text-fg">
                                                {assistant.connectedWhen}
                                            </strong>
                                        </div>
                                    </div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section aria-labelledby={`${baseId}-first`} className="flex flex-col gap-5">
                    <h2 id={`${baseId}-first`} className="font-body text-[24px] font-semibold leading-[1.3] text-fg">
                        2. Ask your first question
                    </h2>
                    <ul className="flex flex-col">
                        {FIRST_QUESTIONS.map((question) => (
                            <li key={question}>
                                <CopyablePrompt text={question} label="question" />
                            </li>
                        ))}
                    </ul>
                    <p className="mt-3 text-center font-body text-[15px] leading-[1.5] text-fg-secondary">
                        Not seeing BARK sources in the answer? Start your question with{' '}
                        <strong className="font-semibold text-fg">"Using BARK"</strong>.
                    </p>
                </section>

                <footer className="text-center font-body text-[15px] leading-[1.5] text-fg-secondary">
                    <p>
                        Stuck? Email{' '}
                        <a
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className={`rounded font-medium text-primary underline underline-offset-[3px] hover:text-primary-hover ${focusRing}`}
                        >
                            {SUPPORT_EMAIL}
                        </a>
                    </p>
                </footer>
            </div>
        </div>
    );
}
